import { useEffect, useMemo, useRef, useState } from 'react';

import type { QltUploadFile } from '../../api/repository/upload';
import { useDynamicDocumentStore } from '../filmstrip/documents';
import { normalizeYoutubeUrl, type YoutubeVideoReference } from '../link/youtube';
import type { ResultSession } from '../results/sessions';
import { useResultSessionStore } from '../results/store';
import type { UploadViewer } from '../upload/validation';
import { useSelectUnit } from '../useSelectUnit';
import { answerDocument, fileDocument, itemLabel, submissionItems, submittedInOrder, type StudentSubmission, type SubmissionItem } from './submissions';
import { useClassSubmissions } from './useClassSubmissions';

export type SubmissionOverlayState =
  | { kind: 'file'; file: QltUploadFile; viewer: UploadViewer }
  | { kind: 'youtube'; video: YoutubeVideoReference }
  | { kind: 'link'; url: string; title: string };

function overlayFor(submission: StudentSubmission, items: SubmissionItem[], index: number): SubmissionOverlayState | null {
  const item = items[index];
  const title = `${submission.studentName} - ${itemLabel(items, index)}`;
  if (item.kind === 'file') {
    const file = item.file;
    return { kind: 'file', viewer: file.viewer, file: { id: `${submission.id}-${index}`, name: title, mime: file.mime, size: file.size, url: file.url } };
  }
  if (item.kind === 'link') {
    const video = normalizeYoutubeUrl(item.url);
    return video ? { kind: 'youtube', video } : { kind: 'link', url: item.url, title };
  }
  return null;
}

// Puts the open student's current part on the board in place of the homework đề: an image or PDF as
// a document the pen writes on, anything else over the submission's painted cover page
export function useHomeworkStudentView(session: ResultSession | null, unitId: string | null) {
  const homework = session && session.kind === 'homework' ? session : null;
  const { roster, tasks } = useClassSubmissions(Boolean(homework));
  const task = homework ? tasks.filter((item) => item.id === homework.refId)[0] || null : null;
  const order = useMemo(() => (task ? submittedInOrder(task, roster) : []), [task, roster]);
  const current = homework && homework.student ? order.filter((item) => item.code === homework.student)[0] || null : null;
  const selectUnit = useSelectUnit();
  // which part's overlay the teacher closed; the overlay itself is derived at render, so its title follows the language
  const [dismissed, setDismissed] = useState<string | null>(null);
  // the effect must not re-run on every render, yet navigate with the current route
  const selectRef = useRef(selectUnit);
  selectRef.current = selectUnit;
  const unitRef = useRef(unitId);
  unitRef.current = unitId;

  const tabId = homework ? homework.tabId : null;
  const item = homework ? homework.item : 0;
  const tick = homework ? homework.tick : -1;

  useEffect(() => {
    if (!tabId || !current) return;
    const items = submissionItems(current);
    const index = Math.min(item, items.length - 1);
    const shown = items[index];
    const page = shown.kind === 'file' ? fileDocument(current, shown.file, index) : null;
    const document = useDynamicDocumentStore.getState().openTransient(page || answerDocument(current));
    // the view first, so the page never shows without the tab that owns it
    useResultSessionStore.getState().setView(tabId, document.id);
    if (unitRef.current !== document.id) selectRef.current({ unitId: document.id, unitName: document.title });
  }, [tabId, current, item, tick]);

  let overlay: SubmissionOverlayState | null = null;
  let overlayKey: string | null = null;
  if (tabId && current) {
    const items = submissionItems(current);
    const index = Math.min(item, items.length - 1);
    const shown = items[index];
    overlayKey = `${tabId}|${current.id}|${index}|${tick}`;
    const page = shown.kind === 'file' ? fileDocument(current, shown.file, index) : null;
    if (!page && overlayKey !== dismissed) overlay = overlayFor(current, items, index);
  }

  return { session: homework, task, order, current, overlay, dismissOverlay: () => setDismissed(overlayKey) };
}
